import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { useCommon, useError } from '../useCommon';
import { IBaseResponse } from '../../types/responses';
import { InventoryController } from '../../services/inventory.api';
import { getInventory, getSelectedLocationId, getSelectedVariant } from '../../store/inventory/selector';
import { inventoryActions } from '../../store/inventory/slice';
import { TPageInfo } from '../../types';
import { TGetProductsByLocation, TTransferVariant, TTransferVariantReq } from '../../types/responses/inventory.type';
import { QUERY_KEYS } from '../../utils/enums';

export function useTransferVariantQuantity() {
  const dispatch = useAppDispatch();
  const authenticateUser = useAppSelector(getAuthenticateUser);
  const selectedVariant = useAppSelector(getSelectedVariant);

  const updatedVariant = async (variant: TTransferVariant) => {
    const updateData : TTransferVariantReq = {
      shopName: authenticateUser.personnel.shopName,
      sourceLocationId: variant.sourceLocationId,
      targetLocationId: variant.targetLocationId,
      quantity: variant.quantity,
      quantityType: variant.quantityType,
      inventoryItemId: variant.inventoryItemId,
    };

    return InventoryController().transferVariantQuntity<IBaseResponse>(updateData);
  };

  return useMutation(updatedVariant, {
    onSuccess: (data) => {
      if (data) {
        dispatch(inventoryActions.setIsTransferWindowOpen(false));
        dispatch(inventoryActions.setUpdatedVariant({
          quantityNumber: selectedVariant?.quantity || 0,
          variantId: selectedVariant?.selectetVariant?.node.id || '',
          locationId: selectedVariant?.selectetVariant?.node.inventoryItem?.inventoryLevel?.location.id || 0,
          quantityTypeName: selectedVariant?.selectedQuantityType as string,
        }));
        dispatch(inventoryActions.setSelectedVariant(null));
      } else {
        console.log('Error on transfer data');
      }
    },
  });
}

export function useGetProductsByLocationFirstPage() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const inventoryItems = useAppSelector(getInventory);
  const { handleReqError } = useError();
  const dispatch = useAppDispatch();
  const { locationsByPermissions } = useCommon();
  const localLocation = selectedLocationId || Number(locationsByPermissions?.[1]?.id);

  return useQuery(
    [QUERY_KEYS.PRODUCTS.GET, localLocation],
    async () => {
      const data = {
        shopName: personnel.shopName,
        locationId: localLocation,
        cursor: null,
      };

      if (localLocation <= 1 || localLocation === null) {
        const tempPageInfo : TPageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        return tempPageInfo;
      }
      if (inventoryItems[localLocation] === undefined) {
        dispatch(inventoryActions.setInventoryLocation(localLocation));
      }

      if (inventoryItems[localLocation]?.isLoading) {
        return inventoryItems[localLocation]?.pageInfo as TPageInfo;
      }
      if (inventoryItems[localLocation]?.pageInfo?.hasNextPage === false) {
        return inventoryItems[localLocation]?.pageInfo;
      }

      const rsp: any = await InventoryController().getAllProductsByLocationId<IBaseResponse & TGetProductsByLocation>(data);
      if ('productVariants' in rsp) {
        dispatch(inventoryActions.setProductsIsLoading({ locationId: selectedLocationId!, isLoading: true }));
        dispatch(inventoryActions.addProductsToLocation({ locationId: selectedLocationId!, products: [], pageInfo: rsp.productVariants.pageInfo }));
        dispatch(inventoryActions.addVariantsToInventory({ locationId: selectedLocationId!, variants: rsp.productVariants.edges }));
        if (!rsp.productVariants.pageInfo.hasNextPage) {
          dispatch(inventoryActions.setProductsIsLoading({ locationId: selectedLocationId!, isLoading: false }));
        }

        return rsp.productVariants.pageInfo as TPageInfo;
      }
      if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get order counts failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      suspense: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      enabled: !!localLocation,
      staleTime: 0,
    },
  );
}
